import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import { $textPrimary, $textTertiary } from '../../../styles/colors/semantics'

// #155 Slice C — Stripe isolated behind a client-only lazy boundary.
//
// The Stripe-dependent inner piece lives in ./StripeMessaging — the ONLY module
// that imports @stripe/*. It is pulled in via React.lazy so @stripe is code-split
// into its own chunk (loaded via <Suspense>) and never referenced at the module
// top level here.
//
// SCOPE NOTE (measured, do not assume a First-Load win): because
// PaymentMethodMessage renders inside ProductCheckoutPage — which is a STATIC
// import in the section registry (pinned static by the #130 fix) — this
// component is in the region/product page's statically-rendered client tree.
// Next.js App Router therefore still emits the code-split @stripe chunk as a
// `<script async>` in the initial HTML (verified via ground-truth browser
// capture: the chunk is fetched on load, before the scroll trigger). ssr:false /
// next/dynamic behaves identically. So this split ISOLATES @stripe into its own
// chunk and defers only its EXECUTION/mount to scroll, but does NOT remove the
// ~17 KB (5.7 KB gzip) of @stripe bytes from First-Load. Actually removing those
// bytes needs the boundary ABOVE the statically-pinned ProductCheckoutPage
// subtree (route/section level), not this leaf. See the slice report.
//
// This is a LEAF util, NOT a section-registry entry, so it does not touch the
// `SectionComponents` object and cannot trip local-rules/no-dynamic-section-
// registry (#132). It cannot cause the #130 streaming-Suspense footer reorder
// either: it is gated behind `visible` (false during SSR and the initial client
// render), so StripeMessaging is never mounted until a post-hydration scroll —
// there is no SSR-vs-client boundary to mismatch (hydration-diff CLEAN).
const StripeMessaging = lazy(() => import('./StripeMessaging'))

// Load the Stripe messaging only once the main thread is IDLE (post-LCP). The
// Klarna/pay-in-4 promo is supplementary text below the price — but mounting it
// pulls js.stripe.com/v3 (~1 MB decoded, 240 KB br) + its main-thread execution,
// and for an above-the-fold price the IntersectionObserver fires on mount, i.e.
// INSIDE the LCP paint window (measured: Stripe.js is the single largest script,
// executing 60-152ms during the shop/pedals render-delay). Deferring the reveal to
// requestIdleCallback moves that download+execution past the paint, so the promo
// still appears (idle fires within ~1s of the page settling; a 2.5s timeout
// guarantees it) but never competes with the LCP. Safari has no requestIdleCallback
// → short setTimeout fallback. Returns a cancel fn for effect cleanup.
function scheduleIdle(cb) {
  if (typeof window === 'undefined') return () => {}
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(cb, { timeout: 2500 })
    return () => window.cancelIdleCallback && window.cancelIdleCallback(id)
  }
  const id = setTimeout(cb, 1500)
  return () => clearTimeout(id)
}

const supportedCountryCodes = ['US', 'CA', 'AU', 'NZ', 'GB', 'IE', 'FR', 'ES', 'DE', 'AT', 'BE', 'DK', 'FI', 'IT', 'NL', 'NO', 'SE', 'CZ', 'GR', 'PT', 'CH', 'PL']
const supportedCurrencies = ['USD', 'GBP', 'EUR', 'DKK', 'NOK', 'SEK', 'AUD', 'CAD', 'NZD', 'PLN', 'CZK', 'CHF']

function PaymentMethodMessage({ isLive, region, countryCode, price, theme, dark, sx }) {
  // LAZY MOUNT: each Stripe <Elements> spins up a cross-origin iframe. A shop
  // grid renders one PaymentMethodMessage per product, so without gating, an
  // N-product page mounts ~2N Stripe iframes at once (a controller + a messaging
  // iframe each). On mobile WebKit that many cross-origin payment iframes —
  // re-instantiated whenever the shipping country changes — triggers a silent
  // renderer memory crash (tab reload → blank page). We therefore only mount
  // Stripe once the card scrolls near the viewport, capping the number of live
  // iframes to what is actually on screen. This scroll gate also defers the
  // @stripe chunk EXECUTION + the js.stripe.com network to post-hydration.
  const [visible, setVisible] = useState(false)
  const containerRef = useRef(null)

  const supported =
    !!price &&
    supportedCountryCodes.includes(countryCode) &&
    supportedCurrencies.includes(price.currency)

  useEffect(() => {
    if (!supported || visible) return
    let cancelIdle = () => {}
    // Reveal at idle, not synchronously: the on-screen IntersectionObserver still
    // caps how many Stripe iframes mount (the WebKit memory-crash guard), but the
    // actual mount — and thus the js.stripe.com load + execution — is deferred to
    // requestIdleCallback so it lands AFTER the LCP paint, not during it.
    const revealWhenIdle = () => {
      cancelIdle = scheduleIdle(() => setVisible(true))
    }
    const el = containerRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      revealWhenIdle()
      return () => cancelIdle()
    }
    // eslint-disable-next-line local-rules/no-handrolled-reveal -- intentional: lazy-MOUNTS the heavy cross-origin Stripe iframe on-screen (see comment above), not an opacity/visibility reveal; content is never gated behind a fade
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect()
          revealWhenIdle()
        }
      },
      { rootMargin: '300px' },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      cancelIdle()
    }
  }, [supported, visible])

  if (!supported) {
    return null
  }

  const appearance = {
    theme: theme || 'stripe',
    variables: {
      colorText: dark ? $textTertiary['dark'] : $textTertiary['light'],
      colorTextSecondary: dark ? $textTertiary['dark'] : $textTertiary['light'], // info icon color
      fontFamily: 'Kanit, sans-serif',
      fontSizeBase: (sx && sx.fontSizeBase) || '16px',
      spacingUnit: (sx && sx.spacingUnit) || '8px',
    },
    rules: {
      '.PaymentMethodMessaging': {
        textAlign: (sx && sx.textAlign) || 'left',
      },
    },
  }

  const options = {
    amount: price.priceCents,
    currency: price.currency,
    // uncomment to display on localhost
    // paymentMethodTypes: ['klarna', 'afterpay_clearpay', 'affirm'],
    // the country that the end-buyer is in
    countryCode: countryCode,
  }

  // The Wrapper is always rendered as the IntersectionObserver target + to
  // reserve layout; the heavy Stripe element mounts only once on-screen. Suspense
  // fallback is null so nothing flashes while the lazy chunk resolves.
  return (
    <div className={styles.wrapper} ref={containerRef}>
      {visible && region && (
        <Suspense fallback={null}>
          <StripeMessaging
            region={region}
            isLive={isLive}
            appearance={appearance}
            options={options}
          />
        </Suspense>
      )}
    </div>
  )
}

export default PaymentMethodMessage
